import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type WriteBatch,
  writeBatch,
} from 'firebase/firestore';
import {
  MemberSchema,
  MemberCreateInputSchema,
  MemberUpdateInputSchema,
  entreeDAnnuaire,
  photoURLValide,
  type Member,
  type MemberCreateInput,
  type MemberUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type ArchivedMode } from './list-query.js';
import { cheminPersonnes, personIdNeuf } from './personnes.js';

/**
 * Repository de la famille `member` : l'annuaire des utilisateurs d'une org et
 * de leurs DROITS (role, derogations par porte). Le modele vit dans
 * `packages/data-model/src/member.ts`, la regle qui fait foi dans
 * `firebase/firestore.rules`.
 *
 * DEUX CHOSES LE DISTINGUENT DES 55 AUTRES REPOSITORIES, et les deux se
 * paient ailleurs si on les oublie :
 *
 * 1. SON IDENTIFIANT EST CHOISI, pas genere : c'est l'email en MINUSCULES
 *    (`emailEnId`). C'est le premier repository du socle dans ce cas, et ce
 *    n'est pas un gout : les Security Rules doivent trouver la fiche de
 *    l'appelant en UN SEUL `get()` a chaque evaluation. Avec un id genere il
 *    faudrait une requete, et les rules ne savent pas requeter. L'id choisi
 *    donne aussi l'unicite gratuitement : deux fiches pour une personne sont
 *    impossibles, la deuxieme ecriture ecraserait la premiere (d'ou le refus
 *    explicite dans `create`).
 *
 * 2. IL VIT DANS UNE SOUS-COLLECTION, `orgs/{orgId}/members`, la seule du socle
 *    avec les secrets. L'org est donc dans le CHEMIN, pas seulement dans un
 *    champ : une requete ne peut structurellement pas sortir de son org, et les
 *    rules comparent un segment de chemin au claim au lieu d'un champ de
 *    document.
 *
 * Le reste suit le patron `project` : entree VALIDEE par Zod avant toute
 * ecriture, champs standard remplis par le SDK, relecture validee par le
 * converter.
 *
 * ET CHAQUE ECRITURE PORTE AUSSI L'ANNUAIRE DES PERSONNES
 * (`orgs/{orgId}/personnes/{personId}`, `personnes.ts`), DANS LE MEME LOT :
 * creer, renommer, desactiver, archiver, supprimer une fiche met son entree a
 * jour en meme temps (`writeBatch`), tout passe ou rien. Pas de trigger : les
 * Functions ne tournent pas partout, et c'est celui qui ecrit la fiche qui sait
 * ce qu'il vient d'ecrire.
 */

export type { MemberCreateInput };

/** Nom de la sous-collection Firestore de la famille member. */
export const MEMBERS_COLLECTION = 'members';

/** Le chemin de l'annuaire d'une org : `orgs/{orgId}/members`. */
function cheminAnnuaire(orgId: string): string {
  return `orgs/${orgId}/${MEMBERS_COLLECTION}`;
}

/**
 * L'IDENTIFIANT d'une fiche membre a partir d'un email : espaces retires,
 * minuscules. Fonction PURE, exportee, parce que c'est la SEULE definition de
 * la correspondance email -> document : les rules la refont de leur cote avec
 * `request.auth.token.email.lower()`, et les deux doivent tomber sur le meme
 * chemin, sinon un utilisateur legitime se voit refuser sa propre fiche.
 *
 * Les deux refus sont fail-fast, et ils ont chacun leur raison :
 *   - sans `@`, ce n'est pas un email : l'appelant s'est trompe d'argument
 *     (un uid, un nom), et ecrire la fiche a ce nom-la la rendrait introuvable
 *     par les rules, donc l'utilisateur perdrait ses droits sans un message ;
 *   - avec un `/`, ce n'est pas un identifiant Firestore : le caractere
 *     DECOUPE le chemin, donc `a/b@x.lu` viserait un autre document (voire une
 *     autre collection) au lieu de lever.
 */
export function emailEnId(email: string): string {
  const id = email.trim().toLowerCase();
  if (!id.includes('@')) {
    throw new Error(
      `emailEnId : "${email}" n'est pas un email (pas de @). L'identifiant d'une fiche ` +
        "membre EST l'email en minuscules : les Security Rules cherchent la fiche a ce " +
        'chemin-la, et a aucun autre.',
    );
  }
  if (id.includes('/')) {
    throw new Error(
      `emailEnId : "${email}" contient un "/", interdit dans un identifiant Firestore ` +
        '(il decoupe le chemin, donc la fiche serait ecrite ailleurs sans erreur).',
    );
  }
  return id;
}

/**
 * Repository type pour la famille `member`.
 * Toute lecture est validee par `MemberSchema`, toute ecriture par
 * `MemberCreateInputSchema` / `MemberUpdateInputSchema`.
 */
export interface MemberRepository {
  /** Cree la fiche d'un email qui n'en a pas encore. */
  create(input: MemberCreateInput): Promise<Member>;
  /**
   * Modifie une fiche, designee par son EMAIL.
   *
   * `apps` et `portes` sont REMPLACES EN BLOC, pas fusionnes clef par clef :
   * `buildUpdateData` ne fait d'exception que pour `customFields`. C'est voulu
   * ici, et c'est meme la seule semantique juste : RETIRER une derogation, ce
   * n'est pas poser une valeur, c'est faire disparaitre une clef. Avec une
   * fusion, une porte fermee par erreur ne pourrait plus jamais etre rouverte
   * autrement qu'en la reglant explicitement. L'appelant envoie donc la map
   * COMPLETE telle qu'il la veut apres coup (c'est ce que fait l'ecran des
   * utilisateurs, qui l'a de toute facon entiere sous les yeux).
   */
  update(email: string, patch: MemberUpdateInput): Promise<Member>;
  /** La fiche d'un email, ou `null`. */
  get(email: string): Promise<Member | null>;
  /** MA fiche : celle de l'utilisateur connecte. `null` sans email ou sans fiche. */
  me(): Promise<Member | null>;
  /** Tout l'annuaire de l'org, trie par email. */
  list(options?: { archived?: ArchivedMode }): Promise<Member[]>;
  /** Archive (`archivedAt`) : l'acces tombe, la fiche reste. Cf. archive-ops.ts. */
  archive(email: string): Promise<Member>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(email: string): Promise<Member>;
  /** Supprime (`deletedAt` + `archivedAt`). Cf. archive-ops.ts. */
  remove(email: string): Promise<Member>;
  /**
   * LA TRACE DU COMPTE A L'ENTREE : ecrit `userId` = l'uid connecte sur MA
   * fiche et sur MON entree d'annuaire, quand elle ne le porte pas encore.
   * Rend `true` si elle a ecrit, `false` sinon (pas de fiche, fiche deja
   * tracee, desactivee ou archivee). Appele par l'hote apres `me()`.
   *
   * POURQUOI COTE CLIENT : `rattacherCompte` (Function) ne tourne que si le
   * jeton n'a pas deja les claims, et les Functions ne sont pas deployees
   * partout ; sans cette trace, un membre deja muni de claims restait a jamais
   * « invite » dans l'annuaire, et `createdBy` ne se nommait pas. Les rules
   * n'autorisent a un membre, sur SA fiche et SON entree, que `userId` (egal a
   * son uid), `updatedAt` et `updatedBy`.
   */
  tracerMonCompte(): Promise<boolean>;
  /**
   * LA PHOTO DE PROFIL A L'ENTREE : range `photoURL` (celle du compte, passee
   * par l'hote : `user.photoURL`) dans MON entree d'annuaire, la seule que les
   * autres membres lisent. Sans elle, ma photo Google n'existe que dans mon
   * navigateur et tous les autres me voient en initiales.
   *
   * N'ECRIT QUE SI CA CHANGE (une entree ne se salit pas d'un `updatedAt` a
   * chaque chargement de page). Une URL qui n'est pas https, vide ou trop
   * longue vaut « pas de photo » (`photoURLValide`) : l'entree la perd, les
   * rules la refuseraient de toute facon. Rend `true` si elle a ecrit, `false`
   * sinon (pas de fiche, fiche sans `personId`, fiche inactive, deja a jour).
   * Les rules n'autorisent a un membre, sur SON entree, que `photoURL`,
   * `updatedAt` et `updatedBy`.
   */
  enregistrerMaPhoto(photoURL: string | null | undefined): Promise<boolean>;
  /**
   * CHANGER L'ADRESSE D'UNE PERSONNE sans lui faire perdre ses fiches : une
   * nouvelle fiche a `nouvelle`, avec le MEME `personId`, le meme role, les
   * memes portes, apps et nom ; l'ancienne fiche archivee ; l'entree
   * d'annuaire suit (`email` = la nouvelle, `userId` = null, jusqu'a la
   * premiere connexion avec la nouvelle adresse). Un seul lot. Rend la
   * nouvelle fiche. Retirer les claims de l'ancien compte reste a l'appelant
   * (`sdk.comptes`), ce n'est pas une ecriture Firestore.
   */
  reprendreSousUneAutreAdresse(email: string, nouvelle: string): Promise<Member>;
}

const memberConverter = createConverter<Member>((raw: DocumentData) => MemberSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifie courant
 *   (injecte par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 * @param getActorEmail - retourne son EMAIL en minuscules, ou `null`. Sert a
 *   `me()` : sans email, il n'y a pas de fiche, et c'est un etat normal (compte
 *   provisionne par script). Les rules font le meme constat de leur cote et
 *   retombent alors sur le role des claims.
 */
export function createMemberRepository(
  db: Firestore,
  getActorId: () => string,
  getActorEmail: () => string | null,
  orgId: string,
): MemberRepository {
  const chemin = cheminAnnuaire(orgId);
  const col = collection(db, chemin).withConverter(memberConverter);
  const cheminDesPersonnes = cheminPersonnes(orgId);

  async function lire(id: string): Promise<Member | null> {
    const snap = await getDoc(doc(col, id));
    if (!snap.exists()) return null;
    return snap.data();
  }

  async function moi(): Promise<Member | null> {
    const email = getActorEmail();
    if (email === null || email === '') return null;
    return lire(emailEnId(email));
  }

  /** La fiche relue apres ecriture : le document reel fait foi, pas la projection. */
  async function relire(id: string, geste: string): Promise<Member> {
    const apres = await lire(id);
    if (apres === null) {
      throw new Error(
        `member ${id} introuvable apres ${geste}. Rechargez la liste : ` +
          'la fiche a disparu entre son ecriture et sa relecture.',
      );
    }
    return apres;
  }

  /** La fiche AVANT ecriture, brute (sans converter) : une fiche historique que le schema refuserait reste modifiable. */
  async function lireBrute(id: string, geste: string): Promise<DocumentData> {
    const snap = await getDoc(doc(db, chemin, id));
    if (!snap.exists()) {
      throw new Error(
        `member ${id} introuvable : rien a ${geste}. ` +
          'La fiche a peut-etre ete supprimee ailleurs, rechargez la liste.',
      );
    }
    return snap.data();
  }

  /**
   * Le `personId` d'une fiche brute, ou `null` pour une fiche historique : elle
   * n'a pas encore d'entree d'annuaire (le backfill la pose), et on n'en
   * invente pas une au detour d'un renommage.
   */
  function personIdDe(brute: DocumentData): string | null {
    const personId: unknown = brute['personId'];
    return typeof personId === 'string' && personId !== '' ? personId : null;
  }

  /** Pose l'entree d'annuaire NEUVE d'une fiche qu'on vient de poser dans le lot. */
  function poserEntree(
    batch: WriteBatch,
    personId: string,
    fiche: { email: string; displayName?: string; userId?: string; disabled?: boolean },
    actor: string,
  ): void {
    const entree = entreeDAnnuaire(fiche, new Date());
    batch.set(doc(db, cheminDesPersonnes, personId), {
      orgId,
      personId,
      displayName: entree.displayName,
      email: entree.email,
      userId: entree.userId,
      // Desactivee des l'invitation : l'entree nait archivee, le selecteur ne
      // la propose pas, son nom reste lisible.
      archivedAt: entree.archivedAt === null ? null : serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: actor,
      updatedBy: actor,
      deletedAt: null,
      schemaVersion: CURRENT_SCHEMA_VERSION,
    });
  }

  /** Met a jour l'entree d'annuaire d'une fiche, dans le lot. */
  function majEntree(batch: WriteBatch, personId: string, patch: DocumentData, actor: string): void {
    batch.update(doc(db, cheminDesPersonnes, personId), {
      ...patch,
      updatedAt: serverTimestamp(),
      updatedBy: actor,
    });
  }

  /**
   * LE PAYLOAD D'UNE FICHE NEUVE, commun a `create` et a la reprise sous une
   * autre adresse : les deux doivent ecrire exactement la meme forme, sinon la
   * fiche reprise differerait d'une fiche creee par un detail que les rules
   * liraient.
   */
  function ficheNeuve(
    fields: Omit<MemberCreateInput, 'personId'>,
    id: string,
    personId: string,
    actor: string,
  ): DocumentData {
    const optional: DocumentData = {};
    if (fields.userId !== undefined) optional['userId'] = fields.userId;
    if (fields.displayName !== undefined) optional['displayName'] = fields.displayName;
    return {
      orgId: fields.orgId,
      // L'email du document EST son identifiant : les rules exigent l'egalite
      // des deux, et une casse differente ferait deux verites.
      email: id,
      // La PERSONNE : posee a la creation, jamais modifiee (rules).
      personId,
      orgRole: fields.orgRole,
      ...optional,
      // Ces trois-la sont ecrits MEME au defaut, jamais laisses absents : les
      // rules lisent `fiche().disabled` et `fiche().portes` sans garde, et une
      // clef manquante y fait echouer la regle, donc refuse tout a la personne.
      disabled: fields.disabled ?? false,
      apps: fields.apps ?? {},
      portes: fields.portes ?? {},
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: actor,
      updatedBy: actor,
      archivedAt: null,
      deletedAt: null,
      schemaVersion: CURRENT_SCHEMA_VERSION,
    };
  }

  /** Refuse de creer une fiche sur un email qui en a deja une. */
  async function refuserSiExiste(id: string): Promise<void> {
    // L'id etant CHOISI, `set` ecraserait une fiche existante sans rien dire,
    // et avec elle les droits qu'elle portait. On refuse au lieu de laisser
    // passer : reinviter quelqu'un ne doit pas lui remettre les droits par
    // defaut. (Course possible entre la lecture et l'ecriture : assumee, la
    // creation d'un membre est un geste rare et humain.)
    const existante = await getDoc(doc(col, id));
    if (existante.exists()) {
      throw new Error(
        `member ${id} existe deja dans l'org ${orgId}. Modifiez sa fiche (update) ` +
          'plutot que de la recreer : la recreer effacerait ses droits actuels.',
      );
    }
  }

  return {
    async create(input: MemberCreateInput): Promise<Member> {
      // Valider AVANT d'ecrire : une entree invalide echoue ici, rien n'atteint
      // Firestore. (Meme doctrine que le patron project.)
      const fields = MemberCreateInputSchema.parse(input);
      const id = emailEnId(fields.email);
      await refuserSiExiste(id);

      /*
       * UN `personId` FOURNI REPREND UNE PERSONNE EXISTANTE. Elle doit exister
       * dans l'annuaire, et n'avoir aucune autre fiche ACTIVE : deux fiches
       * actives pour une personne, ce seraient deux comptes qui se partagent
       * ses heures. Son entree est alors MISE A JOUR, pas recreee (elle garde
       * sa date et son auteur de creation).
       */
      const reprise = fields.personId !== undefined;
      const personId = fields.personId ?? personIdNeuf(db, orgId);
      if (reprise) {
        const entree = await getDoc(doc(db, cheminDesPersonnes, personId));
        if (!entree.exists()) {
          throw new Error(
            `La personne ${personId} n'existe pas dans l'annuaire de l'org ${orgId} : ` +
              "un personId fourni reprend une personne existante, il ne s'invente pas.",
          );
        }
        const actives = await getDocs(
          query(col, where('personId', '==', personId), where('archivedAt', '==', null)),
        );
        const autre = actives.docs[0];
        if (autre !== undefined) {
          throw new Error(
            `Cette personne a deja une fiche active a l'adresse ${autre.id} : ` +
              "archivez-la d'abord, ou changez son adresse depuis sa fiche.",
          );
        }
      }

      const actor = getActorId();
      const now = new Date();
      const payload = ficheNeuve(fields, id, personId, actor);

      const batch = writeBatch(db);
      batch.set(doc(db, chemin, id), payload);
      if (reprise) {
        const entree = entreeDAnnuaire(fields, now);
        majEntree(
          batch,
          personId,
          {
            email: id,
            displayName: entree.displayName,
            userId: entree.userId,
            archivedAt: entree.archivedAt === null ? null : serverTimestamp(),
          },
          actor,
        );
      } else {
        poserEntree(batch, personId, { ...fields, email: id }, actor);
      }
      await batch.commit();

      // Le lot ne resout pas serverTimestamp() : on valide une projection
      // coherente avec ce qui vient d'etre ecrit.
      return MemberSchema.parse({
        id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(email: string, patch: MemberUpdateInput): Promise<Member> {
      const fields = MemberUpdateInputSchema.parse(patch);
      const id = emailEnId(email);
      const actor = getActorId();

      // `apps` et `portes` traversent `buildUpdateData` sans traitement : seul
      // `customFields` y est eclate en chemins pointes. Ces deux maps sont donc
      // REMPLACEES en bloc, ce qui est la semantique voulue (cf. l'interface).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // L'annuaire ne recopie que le nom, l'uid et l'etat : un patch de droits
      // seuls (role, portes, apps) ne le touche pas, et ne coute pas de lecture.
      const pourLAnnuaire: DocumentData = {};
      if (fields.displayName !== undefined) pourLAnnuaire['displayName'] = fields.displayName;
      if (fields.userId !== undefined) pourLAnnuaire['userId'] = fields.userId;
      if (fields.disabled !== undefined) {
        // Desactiver RETIRE la personne du selecteur, sans effacer son nom ;
        // reactiver l'y remet, sauf si la fiche est elle-meme archivee.
        pourLAnnuaire['disabled'] = fields.disabled;
      }

      const batch = writeBatch(db);
      batch.update(doc(db, chemin, id), data);
      if (Object.keys(pourLAnnuaire).length > 0) {
        const avant = await lireBrute(id, 'modifier');
        const personId = personIdDe(avant);
        if (personId !== null) {
          const entree: DocumentData = {};
          if ('displayName' in pourLAnnuaire) entree['displayName'] = pourLAnnuaire['displayName'];
          if ('userId' in pourLAnnuaire) entree['userId'] = pourLAnnuaire['userId'];
          if ('disabled' in pourLAnnuaire) {
            const archivee = avant['archivedAt'] != null;
            if (fields.disabled === true && !archivee) entree['archivedAt'] = serverTimestamp();
            if (fields.disabled === false && !archivee) entree['archivedAt'] = null;
          }
          if (Object.keys(entree).length > 0) majEntree(batch, personId, entree, actor);
        }
      }
      await batch.commit();

      return relire(id, 'mise a jour');
    },

    async get(email: string): Promise<Member | null> {
      return lire(emailEnId(email));
    },

    async me(): Promise<Member | null> {
      return moi();
    },

    async list(options?: { archived?: ArchivedMode }): Promise<Member[]> {
      const mode = options?.archived ?? 'exclude';

      // PAS de `where('orgId', ...)` : l'org est dans le CHEMIN, la
      // sous-collection ne contient qu'elle. Et PAS de tri serveur : un seul
      // filtre d'egalite se sert sur les index automatiques, donc cet annuaire
      // n'exige AUCUN index composite a deployer. Le tri se fait plus bas, en
      // memoire, ce qu'un annuaire d'org permet (quelques dizaines de lignes,
      // jamais paginees).
      const contraintes =
        mode === 'exclude'
          ? [where('archivedAt', '==', null)]
          : mode === 'only'
            ? [where('archivedAt', '!=', null)]
            : [];

      const snap = await getDocs(query(col, ...contraintes));
      return snap.docs
        .map((d) => d.data())
        .sort((a, b) => a.email.localeCompare(b.email));
    },

    /*
     * ARCHIVER, DESARCHIVER, SUPPRIMER : la semantique des trois etats de
     * `archive-ops.ts` (a relire avant de toucher ici : `null` et jamais
     * `deleteField()`, une date d'archivage gardee par la suppression), ecrite
     * a la main parce que l'entree d'annuaire part dans le MEME LOT. Chacun lit
     * la fiche d'abord : il lui faut son `personId`.
     */
    async archive(email: string): Promise<Member> {
      const id = emailEnId(email);
      const avant = await lireBrute(id, 'archiver');
      const actor = getActorId();
      const batch = writeBatch(db);
      batch.update(doc(db, chemin, id), {
        archivedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: actor,
      });
      const personId = personIdDe(avant);
      // Une entree deja archivee (membre desactive) garde sa date.
      if (personId !== null && avant['disabled'] !== true) {
        majEntree(batch, personId, { archivedAt: serverTimestamp() }, actor);
      }
      await batch.commit();
      return relire(id, 'archivage');
    },

    async unarchive(email: string): Promise<Member> {
      const id = emailEnId(email);
      const avant = await lireBrute(id, 'desarchiver');
      const actor = getActorId();
      const batch = writeBatch(db);
      batch.update(doc(db, chemin, id), {
        archivedAt: null,
        updatedAt: serverTimestamp(),
        updatedBy: actor,
      });
      const personId = personIdDe(avant);
      // Desarchivee mais toujours desactivee : elle reste hors du selecteur.
      if (personId !== null && avant['disabled'] !== true) {
        majEntree(batch, personId, { archivedAt: null }, actor);
      }
      await batch.commit();
      return relire(id, 'desarchivage');
    },

    async remove(email: string): Promise<Member> {
      const id = emailEnId(email);
      const avant = await lireBrute(id, 'supprimer');
      const actor = getActorId();
      const patch: DocumentData = {
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: actor,
      };
      const dejaArchivee = avant['archivedAt'] != null;
      // Une fiche deja archivee GARDE sa date d'archivage (archive-ops.ts).
      if (!dejaArchivee) patch['archivedAt'] = serverTimestamp();
      const batch = writeBatch(db);
      batch.update(doc(db, chemin, id), patch);
      const personId = personIdDe(avant);
      // L'entree RESTE (le nom des fiches du passe), archivee ; elle l'est deja
      // si la fiche etait archivee ou desactivee, et garde alors sa date.
      if (personId !== null && !dejaArchivee && avant['disabled'] !== true) {
        majEntree(batch, personId, { archivedAt: serverTimestamp() }, actor);
      }
      await batch.commit();
      return relire(id, 'suppression');
    },

    async tracerMonCompte(): Promise<boolean> {
      const fiche = await moi();
      if (fiche === null) return false;
      const actor = getActorId();
      if (fiche.userId === actor) return false;
      // Les rules refusent la trace a une fiche inactive : inutile d'essayer.
      if (fiche.disabled || fiche.archivedAt !== null || fiche.deletedAt !== null) return false;
      const trace = { userId: actor, updatedAt: serverTimestamp(), updatedBy: actor };
      const batch = writeBatch(db);
      batch.update(doc(db, chemin, fiche.id), trace);
      if (fiche.personId !== undefined) {
        batch.update(doc(db, cheminDesPersonnes, fiche.personId), trace);
      }
      await batch.commit();
      return true;
    },

    async enregistrerMaPhoto(photoURL: string | null | undefined): Promise<boolean> {
      const fiche = await moi();
      if (fiche === null || fiche.personId === undefined) return false;
      if (fiche.disabled || fiche.archivedAt !== null || fiche.deletedAt !== null) return false;
      const voulue = photoURLValide(photoURL);
      const ref = doc(db, cheminDesPersonnes, fiche.personId);
      const entree = await getDoc(ref);
      if (!entree.exists()) return false;
      const actuelle = entree.get('photoURL');
      if ((typeof actuelle === 'string' ? actuelle : null) === voulue) return false;
      const actor = getActorId();
      await updateDoc(ref, { photoURL: voulue, updatedAt: serverTimestamp(), updatedBy: actor });
      return true;
    },

    async reprendreSousUneAutreAdresse(email: string, nouvelle: string): Promise<Member> {
      const ancienId = emailEnId(email);
      const id = emailEnId(nouvelle);
      if (id === ancienId) {
        throw new Error(`La nouvelle adresse est la meme que l'ancienne (${id}) : rien a changer.`);
      }
      const ancienne = await lire(ancienId);
      if (ancienne === null) {
        throw new Error(`member ${ancienId} introuvable dans l'org ${orgId} : rien a reprendre.`);
      }
      if (ancienne.deletedAt !== null) {
        throw new Error(`member ${ancienId} est supprime : il ne se reprend pas sous une autre adresse.`);
      }
      await refuserSiExiste(id);

      const actor = getActorId();
      const now = new Date();
      // Une fiche historique sans `personId` n'a encore aucune fiche d'autre
      // famille qui la designe : la reprise lui en donne un, et son entree.
      const personId = ancienne.personId ?? personIdNeuf(db, orgId);
      const reprise: Omit<MemberCreateInput, 'personId'> = {
        orgId,
        email: id,
        orgRole: ancienne.orgRole,
        disabled: ancienne.disabled,
        apps: ancienne.apps,
        portes: ancienne.portes,
        ...(ancienne.displayName === undefined ? {} : { displayName: ancienne.displayName }),
      };
      const payload = ficheNeuve(reprise, id, personId, actor);

      const batch = writeBatch(db);
      batch.set(doc(db, chemin, id), payload);
      // L'ancienne fiche s'archive : l'ancien compte perd l'acces, la fiche
      // reste lisible par les administrateurs. Sa date d'archivage est gardee.
      batch.update(doc(db, chemin, ancienId), {
        ...(ancienne.archivedAt === null ? { archivedAt: serverTimestamp() } : {}),
        updatedAt: serverTimestamp(),
        updatedBy: actor,
      });
      if (ancienne.personId === undefined) {
        poserEntree(batch, personId, reprise, actor);
      } else {
        // `userId` a null : la personne entrera avec un AUTRE compte, et c'est
        // sa premiere connexion qui le tracera. Une personne desactivee reste
        // hors du selecteur ; sinon elle y revient.
        majEntree(
          batch,
          personId,
          {
            email: id,
            displayName: ancienne.displayName ?? '',
            userId: null,
            ...(ancienne.disabled ? {} : { archivedAt: null }),
          },
          actor,
        );
      }
      await batch.commit();

      return MemberSchema.parse({ id, ...payload, createdAt: now, updatedAt: now });
    },
  };
}
